import { useEffect } from 'react';
import { useNotificationStore, type AppNotification } from '../../store/notifications';

const TONE_STYLE = {
  success: 'border-emerald-200 bg-white text-slate-800',
  info: 'border-blue-200 bg-white text-slate-800',
  error: 'border-red-200 bg-white text-slate-800',
} as const;

export function NotificationCenter() {
  const notifications = useNotificationStore((state) => state.notifications);
  return (
    <aside
      className="pointer-events-none fixed inset-x-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-50 mx-auto flex max-w-md flex-col gap-2 lg:inset-x-auto lg:bottom-6 lg:right-6"
      aria-live="polite"
      aria-label="Notificaciones"
    >
      {notifications.map((notification) => (
        <NotificationToast key={notification.id} notification={notification} />
      ))}
    </aside>
  );
}

function NotificationToast({ notification }: { notification: AppNotification }) {
  const dismiss = useNotificationStore((state) => state.dismiss);
  const runAction = useNotificationStore((state) => state.runAction);

  useEffect(() => {
    if (notification.isActing || notification.error) return;
    const timeout = window.setTimeout(() => dismiss(notification.id), notification.duration);
    return () => window.clearTimeout(timeout);
  }, [dismiss, notification.duration, notification.error, notification.id, notification.isActing]);

  return (
    <div
      className={`pointer-events-auto rounded-2xl border p-3 shadow-[0_16px_34px_-16px_rgba(15,23,42,.38)] ${TONE_STYLE[notification.tone]}`}
      role={notification.tone === 'error' ? 'alert' : 'status'}
    >
      <div className="flex items-center gap-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${notification.tone === 'success' ? 'bg-emerald-100 text-emerald-700' : notification.tone === 'error' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}
          aria-hidden="true"
        >
          {notification.tone === 'success' ? '✓' : notification.tone === 'error' ? '!' : 'i'}
        </span>
        <p className="min-w-0 flex-1 text-sm font-medium leading-5">{notification.message}</p>
        {notification.action && (
          <button
            type="button"
            onClick={() => void runAction(notification.id)}
            disabled={notification.isActing}
            className="tap-target shrink-0 rounded-lg px-2 text-xs font-bold text-savings hover:bg-blue-50 disabled:opacity-60"
          >
            {notification.isActing ? 'Deshaciendo…' : notification.action.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => dismiss(notification.id)}
          className="tap-target -mr-2 shrink-0 rounded-lg px-2 text-lg leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Cerrar notificación"
        >
          ×
        </button>
      </div>
      {notification.error && (
        <p
          role={notification.tone === 'error' ? undefined : 'alert'}
          className="mt-2 pl-10 text-xs text-expense"
        >
          {notification.error}
        </p>
      )}
    </div>
  );
}
